import React, { useEffect, useState } from 'react';
import { bedrockUsageService, UserUsageRow } from '../../services/bedrockUsageService';
import '../../styles/BedrockUsage.css';

function relativeTime(isoString: string): string {
  const now = new Date();
  const date = new Date(isoString);
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays <= 30) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}

function lastModelSegment(modelId: string): string {
  if (!modelId) return '—';
  const idx = modelId.lastIndexOf('/');
  return idx >= 0 ? modelId.slice(idx + 1) : modelId;
}

interface Props {
  toolServerId: number;
  orgId: number;
  orgName: string;
  startDate: string;
  endDate: string;
  onSelectUser: (id: number, username: string) => void;
}

const UserUsageTable: React.FC<Props> = ({ toolServerId, orgId, orgName, startDate, endDate, onSelectUser }) => {
  const [rows, setRows] = useState<UserUsageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    bedrockUsageService
      .getUsersByToolServerOrg(toolServerId, orgId, startDate || undefined, endDate || undefined)
      .then((data) => {
        if (!cancelled) {
          setRows(data);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [toolServerId, orgId, startDate, endDate]);

  if (loading) {
    return (
      <div className="usage-table-container">
        <div className="usage-loading">Loading user usage for {orgName}…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="usage-table-container">
        <div className="usage-empty" style={{ color: 'var(--error-color, #f44336)' }}>
          Error: {error}
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="usage-table-container">
        <div className="usage-empty">No user usage found for {orgName} in the selected period.</div>
      </div>
    );
  }

  return (
    <div className="usage-table-container">
      <table className="usage-table">
        <thead>
          <tr>
            <th>Username</th>
            <th>Total Calls</th>
            <th>Total Tokens</th>
            <th>Via This Server</th>
            <th>Direct</th>
            <th>Top Model</th>
            <th>Top Request Type</th>
            <th>Last Active</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.user_id}>
              <td>
                <span
                  className="usage-clickable-cell"
                  onClick={() => onSelectUser(row.user_id, row.username)}
                >
                  {row.username}
                </span>
              </td>
              <td>{row.total_calls.toLocaleString()}</td>
              <td>{row.sum_actual_tokens.toLocaleString()}</td>
              <td>
                <span className="usage-source-badge primary">{row.via_this_server.toLocaleString()}</span>
              </td>
              <td>
                <span className="usage-source-badge direct">{row.direct_calls.toLocaleString()}</span>
              </td>
              <td title={row.top_model}>{lastModelSegment(row.top_model)}</td>
              <td>{row.top_request_type}</td>
              <td>{relativeTime(row.last_call)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UserUsageTable;
